
import type { Dispatch } from 'redux';

import { createToolbarEvent, sendAnalytics } from '../../analytics';
import { translate } from '../../base/i18n';
import { IconWB } from '../../base/icons';
import { isParticipantModerator } from '../../base/participants';
import { connect } from '../../base/redux';
import {
    AbstractButton,
    type AbstractButtonProps
} from '../../base/toolbox/components';
import { isLocalVideoTrackDesktop } from '../../base/tracks';
import { sendWhiteBoardInstance, setShareMenuVisible } from '../../toolbox/actions.web';

type Props = AbstractButtonProps & {

    /**
     * Whether the genericIFrame is visible or not.
     */
    _visible: boolean,

    /**
     * Redux dispatch function.
     */
    dispatch: Dispatch<any>,

    iframeVisible: boolean,

    _isDisabled: boolean,

    partName: ?string
};

/**
 * Implements an {@link AbstractButton} to open the chat screen on mobile.
 */
class GenericIFrameButton extends AbstractButton<Props, *> {
    accessibilityLabel = 'toolbar.accessibilityLabel.genericIFrame';
    icon = IconWB;
    label = 'toolbar.genericIFrameOpen';

    toggledLabel = this.props.partName || 'Stop Sharing WhiteBoard';


    /**
     * Update the visibility of the {@code OverflowMenuButton}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        if (prevProps._vcString !== this.props._vcString && this.props._vcString !== null) {
            if (this.props._vcString === 'WhiteBoard') {
                this._handleClick();
            }
        }
    }

    /**
     * Handles clicking / pressing the button, and opens / closes the appropriate dialog.
     *
     * @private
     * @returns {void}
     */
    _handleClick() {
        sendAnalytics(
            createToolbarEvent('toggle.genericIFrame', {
                enable: !this.props._visible
            })
        );

        this.props.dispatch(sendWhiteBoardInstance(!this.props.iframeVisible));
        if (navigator.product !== 'ReactNative') {
            this.props.dispatch(setShareMenuVisible(false));
        }

        // this.props.dispatch(sendWhiteBoardInstance(true));
    }

    /**
     * Indicates whether this button is disabled or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isDisabled() {
        return this.props._isDisabled;
    }

    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._visible;
    }
}

/**
 * Maps part of the redux state to the component's props.
 *
 * @param {Object} state - The redux store/state.
 * @param {Object} ownProps - The properties explicitly passed to the component
 * instance.
 * @returns {Object}
 */
function _mapStateToProps(state: Object) {
    const { visible, partId } = state['features/genericiframe'];

    // check if etherpad is active
    const etherpad = state['features/etherpad'];
    const { editing, onEdit } = etherpad;
    const { iframeVisible } = state['features/toolbox'];
    const participants = state['features/base/participants'];
    const participant = participants.filter(i => i.local === true);
    const participantName = participants.filter(i => i.id === partId).map(i => i.name);

    // const localVideo = getLocalVideoTrack(state['features/base/tracks']);
    const videoTrack = isLocalVideoTrackDesktop(state);

    let _isDisabled;
    let partName;

    if (partId) {
        _isDisabled = visible && partId !== participant[0].id;
        partName = visible && partId !== participant[0].id && (isParticipantModerator(participant[0])
            ? `Stop ${participantName}'s WhiteBoard` : `${participantName} is Sharing WhiteBoard`);
    } else {
        _isDisabled = videoTrack || false;
    }
    const { voiceCommandString } = state['features/base/mtApi'];


    return {
        // Toggles the text of the button
        _visible: visible,

        // Hides the Button if no URL was provided.
        visible: true,
        iframeVisible,
        _isDisabled: editing || onEdit ? true : _isDisabled,
        partName,
        _vcString: voiceCommandString
    };
}

export default translate(connect(_mapStateToProps)(GenericIFrameButton));
